import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import FadeIn from '../components/FadeIn';
import { useSiteData } from '../contexts/SiteDataContext';

const ArtistDetails = ({ artistInfo }) => (
  <div className="lg:col-span-4">
    <FadeIn>
      <h2 className="text-xl md:text-2xl font-light text-[#1a1a1a] tracking-[0.01em]">{artistInfo.name}</h2>
      <div className="w-10 h-px bg-[#ddd] mt-5 mb-8" />
      <div className="space-y-5">
        <div>
          <p className="text-[11px] text-[#bbb] mb-1">Based in</p>
          <p className="text-[13px] text-[#666]">Smithtown, Long Island, New York</p>
        </div>
        <div>
          <p className="text-[11px] text-[#bbb] mb-1">Originally from</p>
          <p className="text-[13px] text-[#666]">Donegal, Ireland</p>
        </div>
        <div>
          <p className="text-[11px] text-[#bbb] mb-1">Media</p>
          <p className="text-[13px] text-[#666]">{artistInfo.cv.media.join(', ')}</p>
        </div>
        <div>
          <p className="text-[11px] text-[#bbb] mb-1">Influences</p>
          <p className="text-[13px] text-[#666] leading-relaxed">
            Edward Hopper, Edgar Degas, Vincent van Gogh, Egon Schiele, Alex Kanevsky, Daniel Pitin, Kim Cogan
          </p>
        </div>
        <div>
          <p className="text-[11px] text-[#bbb] mb-1">Collections</p>
          <p className="text-[13px] text-[#666] leading-relaxed">{artistInfo.cv.collections}</p>
        </div>
      </div>
    </FadeIn>
  </div>
);

const CVSection = ({ artistInfo }) => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-16">
    <FadeIn>
      <div className="border-t border-[#eee] pt-16">
        <h2 className="text-lg font-light text-[#1a1a1a] tracking-[0.01em] mb-10">Curriculum Vitae</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-[12px] font-medium text-[#aaa] mb-5">Education</h3>
            <div className="space-y-4">
              {artistInfo.cv.education.map((edu) => (
                <div key={edu.detail}>
                  <p className="text-[14px] text-[#444]">{edu.detail}</p>
                  <p className="text-[12px] text-[#999] mt-0.5">{edu.institution}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-[12px] font-medium text-[#aaa] mb-5">Selected exhibitions</h3>
            <div className="space-y-3">
              {artistInfo.cv.selectedExhibitions.map((ex) => (
                <p key={ex} className="text-[13px] text-[#555] leading-relaxed">{ex}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </FadeIn>
  </section>
);

const About = () => {
  const { artistInfo, isLoading } = useSiteData();
  if (isLoading) return <div className="min-h-[60vh] flex items-center justify-center text-[13px] text-[#aaa]">Loading…</div>;

  return (
    <div data-testid="about-page">
      <section className="relative h-[45vh] md:h-[55vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${artistInfo.portraitImage})` }}>
          <div className="absolute inset-0 bg-black/30" />
        </div>
        <div className="relative z-10 h-full flex items-end">
          <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12 pb-10">
            <h1 className="text-2xl md:text-4xl font-light text-white tracking-[0.01em]">About</h1>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <ArtistDetails artistInfo={artistInfo} />
          <div className="lg:col-span-8">
            <div className="space-y-6">
              {artistInfo.bio.map((paragraph) => (
                <FadeIn key={paragraph.slice(0, 40)}>
                  <p className="text-[15px] md:text-[16px] leading-[1.85] text-[#555] font-light">{paragraph}</p>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CVSection artistInfo={artistInfo} />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-12">
        <FadeIn>
          <div className="border-t border-[#eee] pt-12 flex flex-col sm:flex-row gap-4">
            <Link to="/" className="inline-flex items-center gap-2.5 text-[12px] tracking-[0.02em] text-[#888] hover:text-[#1a1a1a] transition-colors duration-400 border border-[#ddd] hover:border-[#aaa] px-7 py-3">
              View artwork <ArrowRight size={13} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2.5 text-[12px] tracking-[0.02em] text-[#888] hover:text-[#1a1a1a] transition-colors duration-400 border border-[#ddd] hover:border-[#aaa] px-7 py-3">
              Get in touch <ArrowRight size={13} />
            </Link>
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

export default About;
